import {
    CalendarDateFilters,
    FiltersTypes,
} from "@common/types/filters/general";
import { objectKeys, omitBy, pick } from "@common/helpers/objects";
import {
    isTransactionColumn,
    TransactionColumn,
} from "@common/types/views/transactionViews";
import {
    ActiveFilter,
    activeFiltersOrder,
    ActiveFilterToFilterTabsMap,
    FilterTabs,
    isActive,
    resetPayload,
} from "@components/pageHeader/filters/lib";

type TaskFilters = FiltersTypes & CalendarDateFilters;

/**
 * Filter tabs a transaction task can expose to the user, in display order.
 * Only tabs backed by a visible column are actually offered — see
 * `getAvailableTaskFilterTabs`.
 */
const ORDERED_TASK_FILTER_TABS: FilterTabs[] = [
    FilterTabs.DATE,
    FilterTabs.CATEGORY,
    FilterTabs.ACCOUNT,
    FilterTabs.CLASS,
    FilterTabs.AMOUNT,
    FilterTabs.COUNTERPARTY,
    FilterTabs.CUSTOMER,
    FilterTabs.NOTE,
    FilterTabs.RECEIPTS,
    FilterTabs.SPLIT,
    FilterTabs.RECONCILIATION,
    FilterTabs.MATCHED,
    FilterTabs.LEDGER,
    FilterTabs.ACCRUAL_LEDGER,
    FilterTabs.TRANSACTION_LOCKED,
    FilterTabs.REVIEW,
];

/**
 * All transaction-related filter tabs that a task creator could have baked
 * into the task view. Used to detect which filters are creator-set (locked)
 * regardless of whether the corresponding column is visible.
 */
const LOCKABLE_FILTER_TABS = new Set<FilterTabs>([
    ...ORDERED_TASK_FILTER_TABS,
    FilterTabs.RULES,
    FilterTabs.SIMILAR_TO,
    FilterTabs.STATUS,
    FilterTabs.TRANSACTION_IDS,
]);

/** Maps a visible transaction column to the filter tab(s) it enables. */
const COLUMN_FILTER_TABS: Partial<Record<TransactionColumn, FilterTabs[]>> = {
    [TransactionColumn.DATE]: [FilterTabs.DATE],
    [TransactionColumn.AMOUNT]: [FilterTabs.AMOUNT],
    [TransactionColumn.ORIGINAL_AMOUNT]: [FilterTabs.AMOUNT],
    [TransactionColumn.CATEGORY]: [FilterTabs.CATEGORY],
    [TransactionColumn.ACCOUNT]: [FilterTabs.ACCOUNT],
    [TransactionColumn.SUBACCOUNT]: [FilterTabs.ACCOUNT],
    [TransactionColumn.COUNTERPARTY]: [FilterTabs.COUNTERPARTY],
    [TransactionColumn.CUSTOMER]: [FilterTabs.CUSTOMER],
    [TransactionColumn.NOTE]: [FilterTabs.NOTE],
    [TransactionColumn.RECEIPTS]: [FilterTabs.RECEIPTS],
    [TransactionColumn.SPLIT]: [FilterTabs.SPLIT],
    [TransactionColumn.CLASSES]: [FilterTabs.CLASS],
    [TransactionColumn.LEDGER]: [FilterTabs.LEDGER],
    [TransactionColumn.ACCRUAL_LEDGER]: [FilterTabs.ACCRUAL_LEDGER],
    [TransactionColumn.ACCRUAL_REVIEW_LEDGER]: [FilterTabs.ACCRUAL_LEDGER],
    [TransactionColumn.LOCK]: [FilterTabs.TRANSACTION_LOCKED],
    [TransactionColumn.TRANSFER_MATCH]: [FilterTabs.MATCHED],
    [TransactionColumn.RECONCILIATION_STATUS]: [FilterTabs.RECONCILIATION],
    [TransactionColumn.REVIEW]: [FilterTabs.REVIEW],
};

interface LockedTaskFilterState {
    /** Active filters set by the task creator — shown as disabled pills. */
    lockedActiveFilters: ActiveFilter[];
    /** Filter keys controlled by the locked filters. */
    lockedFilterKeys: Array<keyof TaskFilters>;
    /** Locked key/value pairs, re-applied over any user filter change. */
    lockedFilterValues: Partial<TaskFilters>;
}

/**
 * Derives which filters on a transaction task are creator-set and therefore
 * locked: they render as disabled pills, their tabs are removed from the
 * overlay, and their values are forced back on every user filter change.
 *
 * `transactionIds` stay locked for query purposes (sticky snapshots /
 * specific-transaction tasks) but never render as a filter pill.
 */
export function getLockedTaskFilterState(
    baseFilters: TaskFilters,
): LockedTaskFilterState {
    const allLockedActiveFilters = activeFiltersOrder.filter(
        (filterId) =>
            LOCKABLE_FILTER_TABS.has(ActiveFilterToFilterTabsMap[filterId]) &&
            isActive[filterId](baseFilters),
    );

    const controlledFilterKeys = allLockedActiveFilters.reduce<
        Partial<TaskFilters>
    >((acc, filterId) => ({ ...acc, ...resetPayload[filterId] }), {});
    const lockedFilterKeys = objectKeys(controlledFilterKeys);

    const lockedFilterValues = omitBy(
        pick(baseFilters, lockedFilterKeys),
        (value) => value === undefined,
    );

    const lockedActiveFilters = allLockedActiveFilters.filter(
        (filterId) => filterId !== "transactionIds",
    );

    return { lockedActiveFilters, lockedFilterKeys, lockedFilterValues };
}

interface GetAvailableTaskFilterTabsOptions {
    /** Offer the review filter tab. Off for review tasks (select-controlled). */
    includeReviewTab?: boolean;
    /**
     * Whether the workspace is GL-first. GL-first workspaces hide the category
     * filter tab since categories are not used for classification.
     */
    isGlFirst?: boolean;
}

/**
 * Returns the filter tabs a task should offer: only tabs backed by a column
 * that is currently visible in the task view, excluding tabs whose filters
 * were already set by the task creator (those are locked).
 */
export function getAvailableTaskFilterTabs(
    columns: ReadonlyArray<{ id: string }>,
    lockedActiveFilters: readonly ActiveFilter[],
    {
        includeReviewTab = true,
        isGlFirst = false,
    }: GetAvailableTaskFilterTabsOptions = {},
): FilterTabs[] {
    const enabledTabs = new Set<FilterTabs>();
    for (const column of columns) {
        if (!isTransactionColumn(column.id)) {
            continue;
        }
        const tabs = COLUMN_FILTER_TABS[column.id];
        for (const tab of tabs ?? []) {
            enabledTabs.add(tab);
        }
    }

    const lockedTabs = new Set(
        lockedActiveFilters.map(
            (filterId) => ActiveFilterToFilterTabsMap[filterId],
        ),
    );

    return ORDERED_TASK_FILTER_TABS.filter(
        (tab) =>
            enabledTabs.has(tab) &&
            !lockedTabs.has(tab) &&
            (includeReviewTab || tab !== FilterTabs.REVIEW) &&
            (!isGlFirst || tab !== FilterTabs.CATEGORY),
    );
}
